import organicHeader from 'assets/images/signup/organic-header.png';
import { useTranslation } from 'react-i18next';
import { Image, ImageSourcePropType, StyleSheet, View } from 'react-native';
import Illustration, {
  IllustrationSourceType,
} from 'shared/components/vxl/Illustration/Illustration';
import ExpressiveMdText from 'shared/components/vxl/typography/expressive/ExpressiveMdText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import VaroLogo from 'shared/components/vxl/VaroLogo/VaroLogo';
import { isDesktopBrowser } from 'shared/constants/platform';
import theme from 'shared/styles/theme';

export interface SignupHeaderProps {
  headerText: string;
  subHeadText: string;
  headerHero: string;
  headerTextColor: string;
  headerBackgroundColor: string;
  headerSubHeadTextColor: string;
  headerImage: string;
}

const SignupHeader = ({
  headerText,
  subHeadText,
  headerHero,
  headerTextColor,
  headerBackgroundColor,
  headerSubHeadTextColor,
  headerImage,
}: SignupHeaderProps) => {
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'createAccount',
  });

  return (
    <View
      style={[
        styles.headerContainer,
        {
          backgroundColor: headerBackgroundColor || theme.color.expressive.sand,
        },
      ]}
    >
      {headerHero || headerImage ? (
        <View style={headerHero ? styles.header : styles.organicHeader}>
          <ExpressiveMdText
            style={[styles.expressiveHeadingColor, { color: headerTextColor }]}
            numberOfLines={0}
          >
            {translatedText(headerText)}
          </ExpressiveMdText>
          {headerHero ? (
            <View style={styles.imgContainer}>
              <Illustration
                illustration={headerHero as IllustrationSourceType}
                accessibilityLabel={translatedText(
                  'header.headerIllustrationLabel',
                )}
              />
            </View>
          ) : (
            <Image
              style={styles.organicHeaderImageStyle}
              source={organicHeader as ImageSourcePropType}
              accessibilityRole="image"
              accessibilityLabel={translatedText(
                'header.organicHeaderImageLabel',
              )}
              role="img"
            />
          )}
        </View>
      ) : (
        <View style={styles.logoContainer}>
          <VaroLogo width={138} height={52} color="white" />
        </View>
      )}
      {subHeadText ? (
        <BodyBaseText
          style={[
            headerImage ? styles.subHeaderOrganic : styles.subHeader,
            { color: headerSubHeadTextColor },
          ]}
          numberOfLines={0}
        >
          {translatedText(subHeadText)}
        </BodyBaseText>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  expressiveHeadingColor: {
    alignSelf: 'flex-start',
    color: theme.color.expressive.varoPurple,
    flexShrink: 1,
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    paddingBottom: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    paddingRight: isDesktopBrowser ? '25%' : '5%',
    paddingTop: theme.spacing.m,
    textAlign: 'left',
    width: '60%',
  },
  header: {
    flexDirection: 'row',
  },
  headerContainer: {
    backgroundColor: theme.color.expressive.sand,
    display: 'flex',
    flex: 1,
    height: 'auto',
    width: '100%',
  },
  imgContainer: {
    alignItems: 'flex-end',
    height: 109,
    marginEnd: theme.spacing.m,
    width: 145,
  },
  logoContainer: {
    alignSelf: 'center',
  },
  organicHeader: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  organicHeaderImageStyle: {
    flexDirection: 'row',
    height: 150,
    justifyContent: 'flex-end',
    paddingRight: theme.spacing.s,
    resizeMode: 'contain',
    width: '45%',
  },
  subHeader: {
    fontSize: theme.spacing.l,
    marginTop: theme.spacing.s,
    padding: theme.spacing.m,
    textAlign: 'center',
  },
  subHeaderOrganic: {
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
});

export default SignupHeader;
